import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import PhoneInput from 'react-phone-input-2'
import type { CountryData } from 'react-phone-input-2'
import { useNavigate } from 'react-router-dom'
import 'react-phone-input-2/lib/style.css'

import { Button } from '@/components/ui/button'
import { Form, FormControl, FormItem, FormMessage } from '@/components/ui/form'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch } from '@/hooks/redux'
import { setUserPhoneNumber } from '@/store/api/SignUp/SignUp.slice'
import type { signUpPhoneNumberType } from '@/utils/schemas'
import { signUpPhoneNumberSchema } from '@/utils/schemas'
import { WeScreen } from '@/components/WeScreen/WeScreen'

export const SettingsVerifyPhoneNumber = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const form = useForm<signUpPhoneNumberType>({
    resolver: zodResolver(signUpPhoneNumberSchema),
    defaultValues: {
      phoneNumber: '',
    },
  })
  const { handleSubmit, control } = form

  const onSubmit = async (data: signUpPhoneNumberType) => {
    dispatch(setUserPhoneNumber({ phoneNumber: data.phoneNumber }))
    navigate(AppRoutes.settingsVerifyPhoneOTP)
  }

  return (
    <WeScreen useNextButton={false}>
      <div className="flex w-full items-center justify-center h-full">
        <div className="flex flex-col gap-8 max-lg:w-full w-1/3">
          <div className="flex flex-col gap-5 w-full">
            <div className="flex justify-between gap-5 items-center">
              <p className="text-[40px] max-md:text-2xl font-bold tracking-wide max-w-[412px]">
                {t('signUp.phoneNumber.phoneNumber')}
              </p>
            </div>
          </div>

          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-6"
            >
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl className="w-full">
                      <PhoneInput
                        country="us"
                        value={field.value}
                        onChange={(value, country) => {
                          const countryData = country as CountryData
                          const formattedPhone = `+${
                            countryData.dialCode
                          } ${value.slice(countryData.dialCode.length)}`
                          field.onChange(formattedPhone)
                        }}
                        placeholder={t(
                          'dashboard.landingSingUp.singUpFistStep.phoneNumber',
                        )}
                        inputProps={{
                          name: 'phoneNumber',
                          required: true,
                          autoFocus: false,
                        }}
                        containerStyle={{
                          width: '100%',
                          border: '1px solid var(--neutralColor)',
                          backgroundColor: 'var(--baseColor)',
                        }}
                        inputStyle={{
                          width: '100%',
                          height: '44px',
                          borderRadius: '0px 16px 16px 0px',
                          fontSize: '14px',
                          backgroundColor: 'var(--baseColor)',
                          color: 'var(--neutral)',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        }}
                        buttonStyle={{
                          background: 'transparent',
                        }}
                        dropdownStyle={{
                          border: '1px solid var(--neutralColor30)',
                          borderRadius: '8px',
                          backgroundColor: 'var(--baseColor)',
                          boxShadow:
                            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          scrollbarWidth: 'thin',
                        }}
                        searchStyle={{
                          border: '1px solid var(--neutralColor30)',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          backgroundColor: 'var(--baseColor)',
                        }}
                        enableSearch={false}
                        disableSearchIcon={false}
                        preferredCountries={['us', 'ar', 'mx', 'br']}
                        countryCodeEditable={false}
                        enableLongNumbers={true}
                      />
                    </FormControl>
                    <FormMessage data-name="phoneNumber" />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="py-3 px-5 font-medium tracking-widest h-11"
                disabled={!form.formState.isDirty}
              >
                {t('signUp.phoneNumber.continue')}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </WeScreen>
  )
}
